'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import TournamentSponsorItem from './sponser/components/TournamentSponsorItem';
import {
  PATROCINADORES_POR_TORNEIO,
  PatrocinadoresPorTorneioData,
  PatrocinadoresPorTorneioVars,
  criarPatrocinadorTorneio,
  atualizarPatrocinadorTorneio,
  removerPatrocinadorTorneio,
  uploadFotoPatrocinador,
  PatrocinadorTorneio,
} from '../../services/patrocinadorTorneioService';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
} from '../../services/torneioService';

interface SponsorItem extends PatrocinadorTorneio {
  tempId: string;
  file?: File;
}

const AdminTournamentSponserPage: React.FC = () => {
  const { id: torneioId } = useParams<{ id: string }>();
  const { data: torneioData, loading: loadingTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: torneioId || '' },
    skip: !torneioId,
  });

  const {
    data: patrocinadoresData,
    loading: loadingPatrocinadores,
    refetch,
  } = useQuery<PatrocinadoresPorTorneioData, PatrocinadoresPorTorneioVars>(
    PATROCINADORES_POR_TORNEIO,
    {
      variables: { torneioId: torneioId || '' },
      skip: !torneioId,
    },
  );

  const [items, setItems] = useState<SponsorItem[]>([]);
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (patrocinadoresData?.patrocinadoresPorTorneio) {
      const mapped = patrocinadoresData.patrocinadoresPorTorneio.map((p) => ({
        ...p,
        tempId: p.id,
      }));
      setItems(mapped);
    }
  }, [patrocinadoresData]);

  const handleAdd = () => {
    const tempId = `temp-${Date.now()}`;
    const newItem: SponsorItem = {
      tempId,
      nome: '',
      link: '',
      descricao: '',
    } as SponsorItem;
    setItems((prev) => [newItem, ...prev]);
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));
  };

  const markDirty = (tempId: string) =>
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));

  const handleItemChange = (
    tempId: string,
    patch: Partial<PatrocinadorTorneio>,
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...patch } : item,
      ),
    );
    markDirty(tempId);
  };

  const handleUploadPhoto = async (tempId: string, file: File) => {
    setItems((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, file } : item,
      ),
    );
    return URL.createObjectURL(file);
  };

  const handleDelete = async (item: SponsorItem) => {
    if (!window.confirm(`Remover patrocinador ${item.nome || ''}?`)) return;
    const previousItems = [...items];
    setItems((prev) => prev.filter((p) => p.tempId !== item.tempId));
    if (item.id) {
      try {
        await removerPatrocinadorTorneio(item.id);
      } catch (e) {
        alert('Erro ao remover patrocinador');
        setItems(previousItems);
      }
    }
  };

  const handleSave = async () => {
    if (!torneioId) return;
    setSaving(true);
    try {
      const toCreate = items.filter((i) => !i.id);
      const toUpdate = items.filter((i) => i.id && dirtyMap[i.tempId]);

      for (const item of toCreate) {
        const created = await criarPatrocinadorTorneio({
          torneioId: torneioId,
          nome: item.nome,
          link: item.link,
          descricao: item.descricao,
          fotoUrl: item.fotoUrl,
        });
        if (created) {
          if (item.file) {
            const { fotoUrl } = await uploadFotoPatrocinador(
              created.id,
              item.file,
            );
            created.fotoUrl = fotoUrl;
          }
        }
      }

      for (const item of toUpdate) {
        await atualizarPatrocinadorTorneio({
          id: item.id!,
          input: {
            torneioId: torneioId,
            nome: item.nome,
            link: item.link,
            descricao: item.descricao,
            fotoUrl: item.fotoUrl,
          },
        });
        if (item.file && item.id) {
          const { fotoUrl } = await uploadFotoPatrocinador(item.id, item.file);
          item.fotoUrl = fotoUrl;
        }
      }

      setDirtyMap({});
      await refetch();
      alert('Patrocinadores salvos com sucesso');
    } catch (e) {
      alert('Erro ao salvar patrocinadores');
    } finally {
      setSaving(false);
    }
  };

  if (loadingTorneio) return <Load />;

  const torneio = torneioData?.torneioPorId;

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">
                      {torneio?.nome || 'Torneio'}
                    </h4>
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  {torneio?.bannerUrl && (
                    <img
                      src={torneio.bannerUrl}
                      alt="banner"
                      className="w-100 rounded"
                      style={{ maxHeight: 200, objectFit: 'cover' }}
                    />
                  )}
                </div>

                <div className="d-flex justify-content-end mb-3">
                  <button className="btn btn-secondary" onClick={handleAdd}>
                    Novo patrocinador +
                  </button>
                </div>

                {loadingPatrocinadores && <Load />}

                <div className="row g-3">
                  {items.map((item) => (
                    <div key={item.tempId} className="col-12">
                      <TournamentSponsorItem
                        value={item}
                        onChange={(patch) =>
                          handleItemChange(item.tempId, patch)
                        }
                        onUploadPhoto={(file) =>
                          handleUploadPhoto(item.tempId, file)
                        }
                        onDelete={() => handleDelete(item)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AdminTournamentSponserPage;

